import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Contract } from '../../models/Contract'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { Result } from '../../types/utils'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { ContractPage } from './ContractPage'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { SelectList } from '../common/SelectList'

type SelectableContract = {
    contract: Contract
    selected: boolean
}

type ContractsPageProps = {
    contracts: SelectableContract[]
    onSave: (forms: Contract[]) => Promise<Result<any>>
    allowNextWithoutSelection?: boolean
    onClose: () => void
    onBack?: {
        title?: string
        onClick?: () => void
    }
}

export function ContractsPage({
    contracts,
    onSave,
    onClose,
    onBack,
}: ContractsPageProps) {
    const user = PSApplication.me()

    const { logAndDisplayError } = useErrorHandler()

    const [isSaving, setIsSaving] = useState(false)

    const [selectableContracts, setSelectableContracts] = useState<SelectableContract[]>(contracts)

    const [contractToEdit, setContractToEdit] = useState<Contract | undefined>(undefined)

    const selectableContractListItems = useMemo(() => {
        return selectableContracts.map((c) => ({
            id: c.contract.id ?? '',
            title: c.contract.name ?? '',
            subtitle: c.contract.memo,
            tertiaryView: <Icon icon="pen.large" background="round" className="text-primary-700" />,
            selected: c.selected,
            button: c.selected
                ? {
                      title: 'Edit',
                      onClick: () => setContractToEdit(c.contract),
                  }
                : undefined,
        }))
    }, [selectableContracts])

    const industryTitle = user.industry?.name
        ? `${user.industry.name.toLowerCase()}s`
        : 'your industry'

    const onNextHandler = useCallback(async () => {
        setIsSaving(true)
        const result = await onSave(
            selectableContracts.filter((c) => c.selected).map((c) => c.contract)
        )
        setIsSaving(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
        }
    }, [onSave, selectableContracts])

    return (
        <>
            <WorkflowPage
                onClose={onClose}
                onBack={onBack?.onClick}
                backButtonTitle={onBack?.title ?? undefined}
                footerContent={
                    <div className="flex justify-end">
                        <Button
                            intent="primary"
                            type="submit"
                            disabled={isSaving}
                            isLoading={isSaving}
                            onClick={onNextHandler}
                        >
                            Next
                        </Button>
                    </div>
                }
            >
                <Heading size="xl" className="mt-[5px]">
                    Your contracts
                </Heading>
                <Paragraph className="mt-4 max-w-[580px] text-center text-gray-600">
                    These contracts are recommended for {industryTitle}. You can preview and select
                    any you wish from the list below. Add custom contracts later.
                </Paragraph>
                <WorkflowPage.Card className="mt-12">
                    {selectableContracts.length ? (
                        <SelectList
                            selectionType="multi"
                            items={selectableContractListItems}
                            selectedItems={selectableContractListItems.filter((c) => c.selected)}
                            onSelectItems={(items) => {
                                const selectedContractIDs = items.map((c) => c.id)
                                setSelectableContracts((s) =>
                                    s.map((c) => {
                                        if (selectedContractIDs.includes(c.contract.id ?? '')) {
                                            return { ...c, selected: true }
                                        }
                                        return { ...c, selected: false }
                                    })
                                )
                            }}
                        />
                    ) : (
                        <Paragraph size="md" className="text-center text-gray-600">
                            No contracts found
                        </Paragraph>
                    )}
                </WorkflowPage.Card>
            </WorkflowPage>
            {contractToEdit && (
                <ContractPage
                    contract={contractToEdit}
                    onClose={() => setContractToEdit(undefined)}
                    onSave={(updatedContract) => {
                        setSelectableContracts((s) =>
                            s.map((c) =>
                                c.contract.id === updatedContract.id
                                    ? { ...c, contract: updatedContract }
                                    : c
                            )
                        )
                        setContractToEdit(undefined)
                    }}
                />
            )}
        </>
    )
}
